import { createFileRoute } from "@tanstack/react-router";
import { InfoPage } from "@/components/InfoPage";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Picamini.online | Support and Feedback" },
      {
        name: "description",
        content:
          "Contact the Picamini.online team with questions, feedback, accessibility concerns, or reports about our free classroom and PDF tools.",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Contact Picamini.online | Support and Feedback" },
      { property: "og:description", content: "Contact Picamini.online about tools, privacy, accessibility, technical issues, or suggestions." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/contact" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Picamini.online | Support and Feedback" },
      { name: "twitter:description", content: "Reach the Picamini.online team with questions, feedback, and accessibility concerns." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { property: "og:locale", content: "en_US" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/contact" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/contact" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/contact" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://picamini.online/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact",
              "item": "https://picamini.online/contact"
            }
          ]
        })
      }
    ]
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <InfoPage
      eyebrow="Contact"
      title="We are here to help"
      description="Send us a note about a tool, a privacy question, an accessibility concern, or an idea that could make Picamini.online more useful."
    >
      <h2>Contact the Picamini.online team</h2>
      <p>
        We welcome thoughtful feedback from teachers, students, families, school staff, and other
        visitors. The best way to reach us is by email:
      </p>
      <p>
        <strong>Email:</strong> <a href="mailto:support@picamini.online">support@picamini.online</a>
      </p>
      <p>
        Please include the page or tool you are referring to, the device and browser you used,
        and a short description of the issue. Do not send student records, passwords, payment
        details, or other sensitive personal information in your message.
      </p>
      <h2>What we can help with</h2>
      <ul>
        <li>Questions about using a classroom, decision, or PDF tool</li>
        <li>Broken links, display issues, or unexpected tool behavior</li>
        <li>Accessibility feedback and suggestions for clearer controls</li>
        <li>Privacy, advertising, and content concerns</li>
        <li>Suggestions for future tools and improvements</li>
      </ul>
      <h2>Response times</h2>
      <p>
        Picamini.online is a small independent project. We aim to respond within a reasonable
        period, but response times may vary. A contact message does not create a support contract
        or guarantee that a requested feature will be added.
      </p>
    </InfoPage>
  );
}
